import React, { useEffect, useMemo, useState } from "react";
import { useWindowSize } from "react-use";
import { useSwipeable } from "react-swipeable";
import { motion } from "framer-motion";
import PrevBtn from "components/NextPrev/PrevBtn";
import NextBtn from "components/NextPrev/NextBtn";

export default function MySliderCard({
  className = "",
  itemPerRow = 5,
  data = [],
  renderItem = () => <div></div>,
  arrowBtnClass = "top-1/3 -translate-y-1/2",
  autoPlayInterval = 2000,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [numberOfItems, setNumberOfItems] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isJumping, setIsJumping] = useState(false);

  const windowWidth = useWindowSize().width;

  // Liczba elementów widocznych jednocześnie
  useEffect(() => {
    let items = itemPerRow;

    if (windowWidth < 320) {
      items = 1;
    } else if (windowWidth < 620) {
      items = itemPerRow - 3;
    } else if (windowWidth < 1024) {
      items = itemPerRow - 2;
    } else if (windowWidth < 1280) {
      items = itemPerRow - 1;
    }

    items = Math.max(1, Math.min(items, data.length));

    setNumberOfItems(items);
  }, [itemPerRow, windowWidth, data.length]);

  // Trzy kopie danych:
  //
  // A B C D E | A B C D E | A B C D E
  //             ↑
  //           start
  //
  const sortedData = useMemo(() => {
  return [...data].sort((a, b) => Number(b.id) - Number(a.id));
}, [data]);

  // Trzy kopie posortowanych danych
  const extendedData = useMemo(() => {
    return [...sortedData, ...sortedData, ...sortedData];
  }, [sortedData]);

  const dataLength = sortedData.length;

  // Startujemy od środkowej kopii
  useEffect(() => {
    if (!dataLength || !numberOfItems) return;

    setCurrentIndex(dataLength);
  }, [dataLength, numberOfItems]);

  // Czy slider faktycznie może się przesuwać
  const canSlide = dataLength > numberOfItems;

  // Zmiana indeksu
  const changeItemId = (newIndex: number) => {
    if (!canSlide || isJumping) return;

    setCurrentIndex(newIndex);
  };

  // Po zakończeniu animacji sprawdzamy granice
  const handleAnimationComplete = () => {
    if (!canSlide || isJumping) return;

    // Za daleko w lewo
    if (currentIndex <= dataLength - 1) {
      setIsJumping(true);

      setCurrentIndex(currentIndex + dataLength);

      requestAnimationFrame(() => {
        setIsJumping(false);
      });

      return;
    }

    // Za daleko w prawo
    if (currentIndex >= dataLength * 2) {
      setIsJumping(true);

      setCurrentIndex(currentIndex - dataLength);

      requestAnimationFrame(() => {
        setIsJumping(false);
      });
    }
  };

  // Swipe
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (canSlide) {
        changeItemId(currentIndex + 1);
      }
    },

    onSwipedRight: () => {
      if (canSlide) {
        changeItemId(currentIndex - 1);
      }
    },

    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  // Autoplay
  useEffect(() => {
    if (!canSlide || isHovered || isJumping) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }, autoPlayInterval);

    return () => {
      clearInterval(interval);
    };
  }, [
    canSlide,
    isHovered,
    isJumping,
    autoPlayInterval,
  ]);

  // Hover
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Brak danych
  if (!data.length || !numberOfItems) {
    return null;
  }

  return (
    <div
      className={`nc-MySlider ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative"
        {...handlers}
      >
         <div className="overflow-hidden">
        <motion.ul
          initial={false}
          animate={{
            x: `-${currentIndex * (100 / numberOfItems)}%`,
          }}
          transition={
            isJumping
              ? {
                  duration: 0,
                }
              : {
                  x: {
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  },
                }
          }
          onAnimationComplete={handleAnimationComplete}
          className="flex whitespace-nowrap"
        >
          {extendedData.map((item, index) => (
            <li
              key={index}
              className="relative shrink-0 whitespace-normal xl:px-4"
              style={{
                width: `${100 / numberOfItems}%`,
              }}
            >
              {renderItem(item, index % dataLength)}
            </li>
          ))}
        </motion.ul>
        </div>

        {canSlide && (
          <>
            <PrevBtn
              onClick={() => {
                changeItemId(currentIndex - 1);
              }}
              className={`w-9 h-9 xl:w-12 xl:h-12 text-lg absolute -left-4 xl:-left-6 z-[1] ${arrowBtnClass}`}
            />

            <NextBtn
              onClick={() => {
                changeItemId(currentIndex + 1);
              }}
              className={`w-9 h-9 xl:w-12 xl:h-12 text-lg absolute -right-4 xl:-right-6 z-[1] ${arrowBtnClass}`}
            />
          </>
        )}
      </div>
    </div>
  );
}